/* eslint-disable prefer-destructuring */
import React, { useState, useRef, useEffect, useCallback } from 'react';

import classNames from 'classnames';

import Transition from '../Transition';

function findNearestParentWithVerticalScroll(element) {
  let currentElement = element;
  while (currentElement && currentElement !== document.documentElement) {
    // Get the computed style for overflow-y
    const overflowY = window.getComputedStyle(currentElement).overflowY;

    // Check if there's a vertical scrollbar
    const hasVerticalScroll =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      currentElement.scrollHeight > currentElement.clientHeight;

    if (hasVerticalScroll) {
      return currentElement;
    }

    // Move up to the next parent element
    currentElement = currentElement.parentElement;
  }

  // Return null if no element with a vertical scrollbar is found
  return null;
}

export default function DropdownClassic({
  options,
  setFieldValue,
  value,
  name,
  inputRef,
  tabIndex,
  readOnly,
  placeholder,
  fixedPosition,
  fullWidth,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [optionsCoords, setOptionsCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const currentOption = options?.find((option) => option.value === value);

  const dropdown = useRef();
  const searchTimer = useRef();
  const componentRef = useRef();
  const hiddenMeasureRef = useRef();

  const changeOptionsFixedPosition = useCallback(() => {
    const { top, left, width } = componentRef.current.getBoundingClientRect();

    let calculatedWidth = width;

    // Set options width to the widest option if fullWidth is true
    if (hiddenMeasureRef.current && fullWidth) {
      const widest = hiddenMeasureRef.current.offsetWidth + 20;

      // Set the dropdown width to the wider of the input or the widest option
      calculatedWidth = Math.max(width, widest);
    }

    setOptionsCoords({ top: top + 37, left, width: calculatedWidth });
  }, [fullWidth]);

  // get the position of the dropdown options
  useEffect(() => {
    let scrollableElement = null;
    if (dropdownOpen && fixedPosition) {
      changeOptionsFixedPosition();
      scrollableElement = findNearestParentWithVerticalScroll(
        componentRef.current
      );

      if (scrollableElement) {
        scrollableElement.addEventListener(
          'scroll',
          changeOptionsFixedPosition
        );
      }
    } else if (scrollableElement) {
      scrollableElement.removeEventListener(
        'scroll',
        changeOptionsFixedPosition
      );
    }

    return () => {
      if (scrollableElement) {
        scrollableElement.removeEventListener(
          'scroll',
          changeOptionsFixedPosition
        );
      }
    };
  }, [changeOptionsFixedPosition, dropdownOpen, fixedPosition]);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        inputRef.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [dropdownOpen, inputRef]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [dropdownOpen]);

  const scrollOptionIntoView = useCallback(
    () => (option, block) => {
      const optionElement = dropdown.current.querySelector(
        `[data-option="${option.value}"]`
      );

      if (optionElement) {
        optionElement.scrollIntoView({ block: block || 'nearest' });
      }
    },
    []
  );

  const handleKeyPress = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const currentIndex = options.findIndex(
        (option) => option.value === value
      );

      let nextOption = null;

      if (currentIndex === -1) {
        nextOption = options[0];
        setFieldValue(name, nextOption.value);
      } else if (event.key === 'ArrowDown') {
        if (currentIndex === options.length - 1) {
          nextOption = options[0];
          setFieldValue(name, nextOption.value);
        } else {
          nextOption = options[currentIndex + 1];
          setFieldValue(name, nextOption.value);
        }
      } else if (event.key === 'ArrowUp') {
        if (currentIndex === 0) {
          nextOption = options[options.length - 1];
          setFieldValue(name, nextOption.value);
        } else {
          nextOption = options[currentIndex - 1];
          setFieldValue(name, nextOption.value);
        }
      }

      scrollOptionIntoView(nextOption);
      // Search for options based on user input
    } else {
      // Clear search query after 1 second of inactivity
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        setSearchTerm('');
      }, 1000);

      if (event.key.length === 1 && event.key !== ' ') {
        setSearchTerm((prevState) => prevState + event.key);
      }
    }
  };

  // Execute search
  useEffect(() => {
    if (searchTerm?.trim()) {
      const searchResult = options?.find((option) =>
        option.label.toLowerCase().startsWith(searchTerm.toLowerCase())
      );

      if (searchResult && searchResult.value !== value) {
        setFieldValue(name, searchResult.value);
        scrollOptionIntoView(searchResult, 'start');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, options, scrollOptionIntoView, searchTerm, value]);

  const currentPlaceholder = readOnly ? '' : placeholder || 'Select';

  return (
    <div className="relative inline-flex w-full" ref={componentRef}>
      <button
        onKeyDown={handleKeyPress}
        onBlur={() => setDropdownOpen(false)}
        tabIndex={readOnly ? -1 : tabIndex}
        type="button"
        ref={inputRef}
        className={classNames(
          'btn justify-between min-w-[150px] min-h-[37px] w-full bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-500 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-20',
          {
            'border-none shadow-none': readOnly,
          }
        )}
        aria-label="Select date range"
        aria-haspopup="true"
        onClick={(e) => {
          e.preventDefault();
          setDropdownOpen(!dropdownOpen);
        }}
        aria-expanded={dropdownOpen}
      >
        <span className="flex items-center overflow-hidden">
          <span className="truncate">
            {value === '' ? currentPlaceholder : currentOption?.label}
          </span>
        </span>
        {!readOnly && (
          <svg
            className="shrink-0 ml-1 fill-current text-slate-400"
            width="11"
            height="7"
            viewBox="0 0 11 7"
          >
            <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
          </svg>
        )}
      </button>
      <div
        style={{
          position: fixedPosition ? 'fixed' : 'static',
          top: optionsCoords.top,
          left: optionsCoords.left,
          width: fixedPosition ? optionsCoords.width : '',
          zIndex: 1000,
        }}
      >
        <Transition
          show={dropdownOpen}
          tag="div"
          className="z-10 absolute top-full left-0 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden mt-1"
          enter="transition ease-out duration-100 transform"
          enterStart="opacity-0 -translate-y-2"
          enterEnd="opacity-100 translate-y-0"
          leave="transition ease-out duration-100"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
        >
          <div
            ref={dropdown}
            className="font-medium max-h-[250px] overflow-auto text-sm text-slate-600 dark:text-slate-300"
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => setDropdownOpen(false)}
          >
            {options?.map((option) => {
              return (
                <button
                  data-option={option.value}
                  key={option.value}
                  type="button"
                  className={`flex items-center w-full hover:bg-slate-50 hover:dark:bg-slate-700/20 py-1 px-3 text-left cursor-pointer ${
                    option.value === value && 'text-indigo-500'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setDropdownOpen(false);
                    setFieldValue(name, option.value);
                  }}
                >
                  <svg
                    className={`shrink-0 mr-2 fill-current text-indigo-500 ${
                      option.value !== value && 'invisible'
                    }`}
                    width="12"
                    height="9"
                    viewBox="0 0 12 9"
                  >
                    <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
                  </svg>
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </Transition>
      </div>
      {fullWidth && (
        <div
          className="font-medium max-h-[250px] overflow-auto text-sm text-slate-600 dark:text-slate-300"
          ref={hiddenMeasureRef}
          style={{
            position: 'absolute',
            visibility: 'hidden',
            //  whiteSpace: 'nowrap',
          }}
        >
          {options?.map((option) => {
            return (
              <button
                data-option={option.value}
                key={option.value}
                type="button"
                className={`flex items-center w-full hover:bg-slate-50 hover:dark:bg-slate-700/20 py-1 px-3 text-left cursor-pointer ${
                  option.value === value && 'text-indigo-500'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  setDropdownOpen(false);
                  setFieldValue(name, option.value);
                }}
              >
                <svg
                  className={`shrink-0 mr-2 fill-current text-indigo-500 ${
                    option.value !== value && 'invisible'
                  }`}
                  width="12"
                  height="9"
                  viewBox="0 0 12 9"
                >
                  <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
                </svg>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
